<template>
  <div class="w-full max-w-6xl mx-auto space-y-6">
    <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Snapshot Configuration</h1>
        <p class="text-sm text-gray-600 mt-1">Control the public snapshot experience, purchasing rules, and matching options by university or ceremony.</p>
      </div>
      <div class="grid gap-3 w-full md:w-[34rem] md:grid-cols-2">
        <label class="block">
          <span class="text-sm font-medium text-gray-700">University</span>
          <select
            v-model="selectedUniversityId"
            class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            @change="loadUniversity"
          >
            <option value="">Select a university</option>
            <option v-for="uni in universities" :key="uni.id" :value="uni.id">
              {{ uni.name }}
            </option>
          </select>
        </label>
        <label class="block">
          <span class="text-sm font-medium text-gray-700">Scope</span>
          <select
            v-model="scopeMode"
            class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            @change="loadSelected"
          >
            <option value="university">University defaults</option>
            <option value="ceremony">Single ceremony override</option>
          </select>
        </label>
        <label v-if="scopeMode === 'ceremony'" class="block md:col-span-2">
          <span class="text-sm font-medium text-gray-700">Ceremony</span>
          <select
            v-model="selectedCeremonyId"
            class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            @change="loadSelected"
          >
            <option value="">Select a ceremony</option>
            <option v-for="ceremony in ceremonies" :key="ceremony.id" :value="ceremony.id">
              {{ formatCeremony(ceremony) }}
            </option>
          </select>
        </label>
      </div>
    </div>

    <div v-if="error" class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      {{ error }}
    </div>
    <div v-if="success" class="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
      {{ success }}
    </div>

    <div v-if="canEditScope" class="grid lg:grid-cols-3 gap-6">
      <form class="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5 space-y-6" @submit.prevent="saveConfig">
        <section class="space-y-3">
          <div>
            <h2 class="text-base font-semibold text-gray-900">Sales</h2>
            <p class="text-sm text-gray-500">Choose whether graduates pay for digital files and which product prices apply.</p>
          </div>
          <div class="grid md:grid-cols-2 gap-4">
            <label class="flex items-start gap-3 rounded-md border border-gray-200 px-3 py-3">
              <input v-model="config.isFreeDigital" type="checkbox" class="mt-1 h-4 w-4" />
              <span>
                <span class="block text-sm font-medium text-gray-800">Digital files are free</span>
                <span class="block text-xs text-gray-500">Orders with digital items skip Stripe checkout and send the collection email immediately.</span>
              </span>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Gallery type</span>
              <select v-model="config.galleryMode" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option value="snapshot">Graduation snapshots</option>
                <option value="headshot">Professional headshots</option>
              </select>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Price set</span>
              <select v-model="config.pricingTier" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option value="uk">UK products</option>
                <option value="aud">Australia products</option>
              </select>
            </label>
          </div>
        </section>

        <section class="space-y-3 border-t border-gray-100 pt-5">
          <div>
            <h2 class="text-base font-semibold text-gray-900">Public Snapshot Page</h2>
            <p class="text-sm text-gray-500">These settings change what graduates see while finding and selecting photos.</p>
          </div>
          <div class="grid md:grid-cols-2 gap-4">
            <label v-for="field in publicFields" :key="field.key" class="flex items-start gap-3 rounded-md border border-gray-200 px-3 py-3">
              <input v-model="config[field.key]" type="checkbox" class="mt-1 h-4 w-4" />
              <span>
                <span class="block text-sm font-medium text-gray-800">{{ field.label }}</span>
                <span class="block text-xs text-gray-500">{{ field.help }}</span>
              </span>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Selfie prompt</span>
              <select v-model="config.faceSearchRecommended" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option :value="true">Recommend selfie search</option>
                <option :value="false">Make selfie search optional</option>
              </select>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Print options</span>
              <select v-model="config.printsEnabled" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option :value="true">Show print options</option>
                <option :value="false">Hide print options</option>
              </select>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Remember details for</span>
              <div class="mt-1 flex items-center gap-2">
                <input v-model.number="config.rememberMeTtlDays" type="number" min="1" max="365" class="w-28 rounded-md border border-gray-300 px-3 py-2" />
                <span class="text-sm text-gray-500">days</span>
              </div>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Show photos as new for</span>
              <div class="mt-1 flex items-center gap-2">
                <input v-model.number="config.newImagesTtlDays" type="number" min="1" max="365" class="w-28 rounded-md border border-gray-300 px-3 py-2" />
                <span class="text-sm text-gray-500">days</span>
              </div>
            </label>
          </div>
        </section>

        <details class="border-t border-gray-100 pt-5">
          <summary class="cursor-pointer text-base font-semibold text-gray-900">Consent and selfie matching settings</summary>
          <div class="mt-3 grid md:grid-cols-2 gap-4">
            <label class="block md:col-span-2">
              <span class="text-sm font-medium text-gray-700">Consent wording</span>
              <select v-model="config.consentMode" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option value="none">No consent wording</option>
                <option value="ucd_daro">UCD DARO headshot wording</option>
                <option value="custom">Custom consent wording</option>
              </select>
            </label>
            <label v-for="field in consentFields" :key="field.key" class="flex items-start gap-3 rounded-md border border-gray-200 px-3 py-3">
              <input v-model="config[field.key]" type="checkbox" class="mt-1 h-4 w-4" />
              <span>
                <span class="block text-sm font-medium text-gray-800">{{ field.label }}</span>
                <span class="block text-xs text-gray-500">{{ field.help }}</span>
              </span>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Consent form URL</span>
              <input v-model.trim="config.consentUrl" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Consent notification email</span>
              <input v-model.trim="config.consentEmail" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Consent storage reference</span>
              <input v-model.trim="config.consentStorageKey" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label v-if="config.consentMode === 'custom'" class="block md:col-span-2">
              <span class="text-sm font-medium text-gray-700">Custom consent title</span>
              <input v-model.trim="config.consentTitle" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label v-if="config.consentMode === 'custom'" class="block md:col-span-2">
              <span class="text-sm font-medium text-gray-700">Custom consent body</span>
              <textarea v-model.trim="config.consentBody" rows="5" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"></textarea>
            </label>
            <label v-if="config.consentMode === 'custom'" class="block md:col-span-2">
              <span class="text-sm font-medium text-gray-700">Custom consent checkbox text</span>
              <input v-model.trim="config.consentCheckboxText" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Selfie consent version</span>
              <input v-model.trim="config.saveSelfieConsentVersion" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Selfie privacy policy URL</span>
              <input v-model.trim="config.saveSelfieConsentPolicyUrl" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Organisation shown in consent text</span>
              <input v-model.trim="config.saveSelfieConsentOrgName" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
          </div>
        </details>

        <div class="flex items-center gap-3">
          <button
            type="submit"
            class="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            :disabled="saving || loading"
          >
            {{ saving ? 'Saving...' : 'Save configuration' }}
          </button>
          <span v-if="updatedAt" class="text-xs text-gray-500">Last saved {{ formatDate(updatedAt) }}</span>
        </div>
      </form>

      <div class="space-y-4">
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <h2 class="font-semibold text-gray-900 mb-1">Snapshot Orders</h2>
          <p class="text-xs text-gray-500 mb-3">Recent digital and print bundle activity for this university.</p>
          <dl class="grid grid-cols-2 gap-3 text-sm">
            <div v-for="card in summaryCards" :key="card.label" class="rounded-md border border-gray-200 p-3">
              <dt class="text-gray-500">{{ card.label }}</dt>
              <dd class="text-2xl font-semibold text-gray-900">{{ card.value }}</dd>
            </div>
          </dl>
        </div>
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <h2 class="font-semibold text-gray-900 mb-2">Current Scope</h2>
          <p class="text-sm text-gray-700">{{ scopeTitle }}</p>
          <p class="text-xs text-gray-500 mt-1">{{ scopeSubtitle }}</p>
          <p v-if="scopeMode === 'ceremony'" class="mt-2 text-xs text-gray-500">
            Ceremony settings inherit the university defaults and override only this ceremony.
          </p>
        </div>
      </div>
    </div>

    <div v-if="canEditScope" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 class="font-semibold text-gray-900">Recent Snapshot Orders</h2>
        <button class="text-sm text-blue-700 hover:text-blue-900" type="button" @click="loadDownloads">Refresh</button>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 text-left text-gray-500">
            <tr>
              <th class="px-4 py-2 font-medium">Order</th>
              <th class="px-4 py-2 font-medium">Downloads</th>
              <th class="px-4 py-2 font-medium">Files</th>
              <th class="px-4 py-2 font-medium">Last downloaded</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="item in downloads" :key="item.order_id" class="align-top">
              <td class="px-4 py-3 font-mono text-xs">{{ item.order_nano_id }}</td>
              <td class="px-4 py-3">{{ item.download_count }}</td>
              <td class="px-4 py-3 max-w-md">
                <div v-for="file in item.included_files" :key="`${item.order_id}-${file.product_type}-${file.file_name}`" class="text-xs">
                  <span class="font-medium">{{ productLabel(file.product_type) }}</span>: {{ file.file_name }}
                </div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-600">{{ formatDate(item.last_downloaded_at || item.collected_at) }}</td>
            </tr>
            <tr v-if="!downloads.length">
              <td class="px-4 py-5 text-gray-500" colspan="4">No snapshot orders found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { trackEvent } from '../../Analytics.vue'

const DEFAULT_CONFIG = {
  requiresConsent: false,
  consentUrl: '',
  consentEmail: '',
  consentStorageKey: '',
  consentTitle: '',
  consentBody: '',
  consentCheckboxText: '',
  isFreeDigital: false,
  isHeadshot: false,
  galleryMode: 'snapshot',
  printsEnabled: true,
  faceSearchRecommended: true,
  consentMode: 'none',
  ceremonyLabelOnly: false,
  pricingTier: 'uk',
  rememberMeEnabled: true,
  saveSelfieEnabled: false,
  rememberMeTtlDays: 30,
  newImagesTtlDays: 7,
  saveSelfieConsentVersion: 'v1',
  saveSelfieConsentPolicyUrl: '/privacy-policy',
  saveSelfieConsentOrgName: 'MyGraduationClip',
}

export default {
  name: 'SnapshotConfiguration',
  data() {
    return {
      universities: [],
      selectedUniversityId: '',
      ceremonies: [],
      selectedCeremonyId: '',
      scopeMode: 'university',
      config: { ...DEFAULT_CONFIG },
      summary: {},
      downloads: [],
      loading: false,
      saving: false,
      error: '',
      success: '',
      updatedAt: null,
      publicFields: [
        { key: 'ceremonyLabelOnly', label: 'Use ceremony labels only', help: 'Shows the ceremony label instead of full ceremony date/time copy.' },
        { key: 'rememberMeEnabled', label: 'Remember graduate details', help: 'Lets the browser remember name, email, and selfie state for return visits.' },
      ],
      consentFields: [
        { key: 'requiresConsent', label: 'Require consent before search', help: 'Ask graduates to accept consent terms before they can search for photos.' },
        { key: 'saveSelfieEnabled', label: 'Allow saved selfie matching', help: 'Let graduates save a selfie subscription for later photo match notifications.' },
      ],
    }
  },
  computed: {
    selectedUniversity() {
      return this.universities.find((u) => u.id === this.selectedUniversityId) || null
    },
    selectedUniversityName() {
      return this.selectedUniversity ? this.selectedUniversity.name : ''
    },
    selectedUniversitySlug() {
      return this.selectedUniversity ? this.selectedUniversity.slug : ''
    },
    selectedCeremony() {
      return this.ceremonies.find((c) => c.id === this.selectedCeremonyId) || null
    },
    canEditScope() {
      if (!this.selectedUniversityId) return false
      return this.scopeMode !== 'ceremony' || Boolean(this.selectedCeremonyId)
    },
    scopeTitle() {
      if (this.scopeMode === 'ceremony') return this.formatCeremony(this.selectedCeremony)
      return this.selectedUniversityName
    },
    scopeSubtitle() {
      if (this.scopeMode === 'ceremony') return `${this.selectedUniversityName} · ${this.selectedCeremonyId}`
      return this.selectedUniversitySlug
    },
    configEndpoint() {
      const base = `${this.apiBase()}/admin/snapshot/config/universities/${this.selectedUniversityId}`
      if (this.scopeMode === 'ceremony') return `${base}/ceremonies/${this.selectedCeremonyId}`
      return base
    },
    downloadsEndpoint() {
      return `${this.configEndpoint}/downloads`
    },
    summaryCards() {
      return [
        { label: 'Orders', value: this.summary.ordered_bundles || 0 },
        { label: 'Downloaded', value: this.summary.downloaded_orders || 0 },
        { label: 'Download events', value: this.summary.download_events || 0 },
        { label: 'Digital items', value: this.summary.digital_items || 0 },
      ]
    },
  },
  created() {
    this.loadUniversities()
    trackEvent(this.$route.path, { label: 'SNAPSHOT_CONFIG_OPENED' })
  },
  methods: {
    apiBase() {
      return process.env.VUE_APP_SNAPSHOT_API_BASE || ''
    },
    async loadUniversities() {
      try {
        const r = await axios.get('/universities')
        this.universities = r.data || []
        if (this.universities.length && !this.selectedUniversityId) {
          this.selectedUniversityId = this.universities[0].id
          await this.loadUniversity()
        }
      } catch (e) {
        this.error = 'Unable to load universities.'
      }
    },
    async loadUniversity() {
      this.selectedCeremonyId = ''
      this.ceremonies = []
      if (!this.selectedUniversityId) return
      this.error = ''
      try {
        const r = await axios.get(`/universities/${this.selectedUniversityId}/ceremonies`)
        this.ceremonies = r.data || []
      } catch (e) {
        this.error = 'Unable to load ceremonies.'
      }
      await this.loadSelected()
    },
    async loadSelected() {
      if (!this.canEditScope) return
      this.error = ''
      this.success = ''
      this.loading = true
      try {
        const [configResponse, downloadsResponse] = await Promise.all([
          axios.get(this.configEndpoint),
          axios.get(this.downloadsEndpoint),
        ])
        this.config = { ...DEFAULT_CONFIG, ...(configResponse.data.config || {}) }
        this.normalizeCompatibilityConfig()
        this.updatedAt = configResponse.data.updated_at || null
        this.summary = downloadsResponse.data.summary || {}
        this.downloads = downloadsResponse.data.items || []
      } catch (e) {
        this.error = 'Unable to load snapshot configuration.'
      } finally {
        this.loading = false
      }
    },
    async loadDownloads() {
      if (!this.canEditScope) return
      try {
        const r = await axios.get(this.downloadsEndpoint)
        this.summary = r.data.summary || {}
        this.downloads = r.data.items || []
      } catch (e) {
        this.error = 'Unable to load bundle downloads.'
      }
    },
    async saveConfig() {
      if (!this.canEditScope) return
      this.saving = true
      this.error = ''
      this.success = ''
      try {
        this.normalizeCompatibilityConfig()
        const r = await axios.put(
          this.configEndpoint,
          { config: this.config }
        )
        this.config = { ...DEFAULT_CONFIG, ...(r.data.config || {}) }
        this.normalizeCompatibilityConfig()
        this.success = 'Snapshot configuration saved.'
        trackEvent(this.$route.path, {
          label: 'SNAPSHOT_CONFIG_SAVED',
          university_id: this.selectedUniversityId,
          ceremony_id: this.scopeMode === 'ceremony' ? this.selectedCeremonyId : null,
          scope: this.scopeMode,
        })
        await this.loadDownloads()
      } catch (e) {
        this.error = 'Unable to save snapshot configuration.'
      } finally {
        this.saving = false
      }
    },
    formatDate(value) {
      if (!value) return ''
      try { return new Date(value).toLocaleString() } catch (e) { return String(value) }
    },
    formatCeremony(ceremony) {
      if (!ceremony) return ''
      if (ceremony.label) return ceremony.label
      const value = ceremony.datetime || ceremony.ceremony_datetime || ceremony.id || ''
      try { return new Date(value).toLocaleString() } catch (e) { return String(value) }
    },
    normalizeCompatibilityConfig() {
      if (!this.config.galleryMode || this.config.galleryMode === 'snapshot') {
        this.config.galleryMode = this.config.isHeadshot ? 'headshot' : 'snapshot'
      }
      this.config.isHeadshot = this.config.galleryMode === 'headshot'
    },
    productLabel(value) {
      if (value === 'DIGITAL') return 'Digital'
      if (value === 'PRINT') return 'Print'
      return value || 'Item'
    },
  },
}
</script>
